import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, FileSpreadsheet, Pencil, Plus, Trash2 } from 'lucide-react';
import { useData } from '../../App';
import { Button, Card, Input, Modal, Select } from '../UI';
import { BarChart, SERIES_COLORS } from '../BarChart';
import { ALL_ORGS, BillingSummary, Debtor, EndCustomer, Invoice, OpeningDebt, Payment, PaymentSchedule, Project } from '../../types';
import { api } from '../../services/api';
import { formatDay, todayISO } from '../../utils/format';
import { csvMoney, downloadCsv, errorText, Money, money, Td, Th, useNames, useScope } from './shared';
import { confirmDialog, promptDialog } from '../Dialogs';

// Контрагент, с которым больше всего договоров (обычно Ланстрой) — по умолчанию в сводках
export const useMainCounterparty = (contractsCp: string[]) => {
    const { state } = useData();
    return useMemo(() => {
        const counts: Record<string, number> = {};
        contractsCp.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0]
            || state.counterparties.find(c => c.type === 'LEGAL_ENTITY')?.id || '';
    }, [contractsCp, state.counterparties]);
};

// --- Сводка по контрагенту (лист «Главная») ---
export const SummaryTab: React.FC<{ endCustomers: EndCustomer[]; mainCp: string }> = ({ endCustomers, mainCp }) => {
    const { state, revision, showNotification } = useData();
    const names = useNames();
    const scope = useScope();
    const [cpId, setCpId] = useState('');
    const [year, setYear] = useState(new Date().getFullYear());
    const [data, setData] = useState<BillingSummary | null>(null);
    useEffect(() => { if (!cpId && mainCp) setCpId(mainCp); }, [mainCp, cpId]);
    useEffect(() => {
        if (!cpId) return;
        let alive = true;
        api.getBillingSummary(cpId, year, scope).then(d => { if (alive) setData(d); }).catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [cpId, year, scope, revision]);
    const ecName = (id: string | null) => endCustomers.find(e => e.id === id)?.name || 'Без конечного заказчика';
    const reconcile = async (orgId: string) => {
        try {
            const r = await api.getReconciliation(orgId, cpId, `${year}-01-01`, `${year}-12-31`);
            window.open(api.fileUrl(r.fileName), '_blank');
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    const rows: [string, number, string, boolean?][] = data ? [
        ['Долг на начало года', data.opening, 'входящий остаток'],
        ['Выполнено за год', data.done, 'выполненные работы с НДС'],
        ['Всего к оплате', data.totalDue, 'долг на начало + выполнено', true],
        ['Выставлено счетов', data.invoiced, ''],
        ['Оплачено', data.paid, `по счетам ${money(data.paidInvoices)} + долг прошлых лет ${money(data.paidOpening)} + на объекты без счёта ${money(data.paidProjects || 0)}`],
        ['Выполнено, но не выставлено', data.notInvoiced,
         data.notInvoiced < 0 ? 'выполнено − выставлено; минус — есть счета без внесённых работ (или за работы прошлого года)' : 'выполнено − выставлено', true],
        ['Выставлено, но не оплачено', data.invoicedNotPaid, 'выставлено − оплачено по счетам', true],
        ['Осталось получить', data.remaining, 'всего к оплате − оплачено', true],
        ['Переплата (аванс)', data.overpayment, 'нераспределённые оплаты'],
    ] : [];
    return (
        <div className="space-y-4">
            <Card className="bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <Select label="Контрагент" value={cpId} onChange={e => setCpId(e.target.value)}
                        options={state.counterparties.filter(c => !c.archivedAt && c.type === 'LEGAL_ENTITY' || c.type === 'ENTREPRENEUR').map(c => ({ value: c.id, label: c.name }))} />
                    <Input label="Год" type="number" value={year} onChange={e => setYear(Number(e.target.value))} />
                    <p className="text-xs text-slate-500">Охват — из шапки: {scope === ALL_ORGS ? 'все ИП' : names.org(scope)}</p>
                </div>
            </Card>
            {data && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody className="divide-y divide-slate-100">
                                {rows.map(([label, v, hint, strong]) => (
                                    <tr key={label} className={strong ? 'bg-slate-50' : ''}>
                                        <Td className={strong ? 'font-semibold' : ''}>{label}<div className="text-[11px] text-slate-400">{hint}</div></Td>
                                        <Td right className={strong ? 'font-bold' : ''}><Money value={v} tone={label.startsWith('Осталось') || label.includes('не ') ? 'debt' : undefined} /></Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="space-y-4">
                        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-600"><tr><Th>ИП</Th><Th right>Выставлено</Th><Th right>Оплачено</Th><Th right>Не оплачено</Th><Th /></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.byOrg.length === 0 && <tr><Td colSpan={5} className="text-center text-slate-400">Нет счетов</Td></tr>}
                                    {data.byOrg.map(r => (
                                        <tr key={r.organizationId}><Td>{names.orgShort(r.organizationId)}</Td><Td right><Money value={r.invoiced || 0} /></Td>
                                            <Td right><Money value={r.paid || 0} tone="good" /></Td><Td right><Money value={r.unpaid} tone="debt" /></Td>
                                            <Td right><button className="text-xs text-blue-600 hover:underline whitespace-nowrap" onClick={() => reconcile(r.organizationId)}><FileSpreadsheet className="inline h-3 w-3" /> сверка</button></Td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-600"><tr><Th>Конечный заказчик</Th><Th right>Выполнено</Th><Th right>Выставлено</Th><Th right>Оплачено</Th><Th right>Не оплачено</Th></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.byEndCustomer.map(r => (
                                        <tr key={r.endCustomerId || '-'}><Td>{ecName(r.endCustomerId)}</Td><Td right><Money value={r.done || 0} /></Td><Td right><Money value={r.invoiced || 0} /></Td>
                                            <Td right><Money value={r.paid || 0} tone="good" /></Td><Td right><Money value={r.unpaid} tone="debt" /></Td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-slate-500">Акт сверки — Excel за {year} год: долг на начало, акты, оплаты, конечное сальдо.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- График оплат: ожидаемые поступления по неделям и факт на той же шкале ---
export const ScheduleTab: React.FC = () => {
    const { revision } = useData();
    const names = useNames();
    const scope = useScope();
    const [data, setData] = useState<PaymentSchedule | null>(null);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const load = useCallback(() => {
        let alive = true;
        api.getSchedule(scope).then(d => { if (alive) setData(d); }).catch(() => { if (alive) setData(null); });
        api.getInvoices({ scope, unpaid: true }).then(d => { if (alive) setInvoices(d); }).catch(() => { if (alive) setInvoices([]); });
        return () => { alive = false; };
    }, [scope]);
    useEffect(load, [load, revision]);
    const [reload, setReload] = useState(0);
    useEffect(() => { if (reload) load(); }, [reload, load]);
    if (!data) return <div className="text-sm text-slate-400">Загрузка…</div>;
    const weekLabel = (w: string) => `${w.slice(8, 10)}.${w.slice(5, 7)}`;
    const sorted = [...invoices].sort((a, b) => a.expectedDate.localeCompare(b.expectedDate));
    const edit = async (inv: Invoice) => {
        const v = await promptDialog('Ожидаемая дата оплаты', inv.expectedManual ? inv.expectedDate : '', { type: 'date', hint: 'Пусто — по сроку договора' });
        if (v === null) return;
        await api.setExpected(inv.id, v.trim() || null).catch(() => null);
        setReload(r => r + 1);
    };
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-white rounded-lg border border-slate-200 p-3"><div className="text-[11px] text-slate-500">Ожидается в ближайшие 14 дней</div><div className="text-lg font-bold"><Money value={data.next14} /> ₽</div></div>
                <div className="bg-white rounded-lg border border-red-200 p-3"><div className="text-[11px] text-slate-500">Просрочено</div><div className="text-lg font-bold"><Money value={data.overdue} tone="debt" /> ₽</div></div>
            </div>
            <Card title="По неделям: ожидается и поступило">
                <BarChart data={data.weeks.map(w => ({ label: weekLabel(w.week), values: { expected: w.expected + w.overdue, paid: w.paid } }))}
                          series={[{ key: 'expected', name: 'Ожидается (просроченное — на текущей неделе)', color: SERIES_COLORS[1] },
                                   { key: 'paid', name: 'Поступило', color: SERIES_COLORS[0] }]} height={170} />
            </Card>
            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600"><tr><Th>Ожидаем</Th><Th>Счёт</Th><Th>От кого</Th><Th>ИП</Th><Th right>Сумма</Th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {sorted.length === 0 && <tr><Td colSpan={5} className="text-center text-slate-400 py-6">Неоплаченных счетов нет</Td></tr>}
                        {sorted.map(inv => (
                            <tr key={inv.id} className={inv.overdueDays ? 'bg-red-50/40' : ''}>
                                <Td className="whitespace-nowrap"><button onClick={() => edit(inv)} className={`hover:underline ${inv.overdueDays ? 'text-red-600 font-semibold' : ''}`} title="Изменить ожидаемую дату">
                                    {formatDay(inv.expectedDate)}{inv.expectedManual ? ' ✎' : ''}</button>
                                    {inv.overdueDays > 0 && <div className="text-[11px] text-red-600">просрочено {inv.overdueDays} дн.</div>}</Td>
                                <Td className="text-xs">№{inv.number} от {formatDay(inv.date)}{inv.appendixNumber ? ` · прил. №${inv.appendixNumber}` : ''}</Td>
                                <Td className="text-xs">{names.cp(inv.counterpartyId)}</Td>
                                <Td className="text-xs">{names.orgShort(inv.organizationId)}</Td>
                                <Td right><Money value={inv.remaining} tone={inv.overdueDays ? 'debt' : undefined} /></Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-slate-500">Ожидаемая дата — дата акта (или счёта) + срок оплаты из договора; её можно изменить вручную, например «по 20-е число». Эти суммы попадают в строку «Ожидается» во вкладке «Планирование».</p>
        </div>
    );
};

// --- Кто нам должен ---
export const DebtorsTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
    const { revision } = useData();
    const names = useNames();
    const scope = useScope();
    const [data, setData] = useState<{ items: Debtor[]; total: number; notFormalized: number } | null>(null);
    useEffect(() => {
        let alive = true;
        api.getDebtors(scope).then(d => { if (alive) setData(d); }).catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [scope, revision]);
    const pName = (id: string) => projects.find(p => p.id === id)?.name || '—';
    const exportCsv = () => downloadCsv(`debtors_${todayISO()}.csv`, [
        ['Контрагент', 'Долг', 'Не оформлено (с НДС)', 'Самый старый неоплаченный счёт', 'Дней', 'По ИП', 'Объекты'],
        ...(data?.items || []).map(d => [d.customerName, csvMoney(d.debt), csvMoney(d.notFormalized), formatDay(d.oldestUnpaidDate), d.oldestDays ?? '',
            d.byOrg.map(o => `${names.orgShort(o.organizationId)}: ${csvMoney(o.debt)}`).join(', '), d.projectIds.map(pName).join(', ')])]);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <div className="text-sm">Нам должны: <strong className="text-red-600"><Money value={data?.total || 0} /> ₽</strong>
                    <span className="text-slate-500"> · выполнено, но не оформлено: <Money value={data?.notFormalized || 0} /> ₽</span></div>
                <Button variant="outline" size="sm" onClick={exportCsv}><Download className="mr-1 h-4 w-4" /> CSV (Excel)</Button>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600"><tr><Th>Контрагент</Th><Th right>Долг</Th><Th right>Не оформлено</Th><Th right>Самый старый счёт</Th><Th>По ИП</Th><Th>Объекты</Th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {(data?.items || []).length === 0 && <tr><Td colSpan={6} className="text-center text-slate-400 py-6">Долгов нет</Td></tr>}
                        {(data?.items || []).map(d => (
                            <tr key={d.customerId || d.customerName}>
                                <Td className="font-medium">{d.customerName}</Td>
                                <Td right><Money value={d.debt} tone="debt" /></Td>
                                <Td right><Money value={d.notFormalized} /></Td>
                                <Td right className={`text-xs whitespace-nowrap ${(d.oldestDays ?? 0) > 30 ? 'text-red-600 font-bold' : ''}`}>{d.oldestUnpaidDate ? `${formatDay(d.oldestUnpaidDate)} · ${d.oldestDays} дн.` : '—'}</Td>
                                <Td className="text-xs">{d.byOrg.map(o => <div key={o.organizationId}>{names.orgShort(o.organizationId)}: <Money value={o.debt} /></div>)}</Td>
                                <Td className="text-xs">{d.projectIds.map(id => <div key={id}><Link to={`/projects/${id}`} className="text-blue-600 hover:underline">{pName(id)}</Link></div>)}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Объекты, на которые можно разнести оплату контрагента: его объекты (или без генподрядчика), по названию
const projectsFor = (projects: Project[], cpId: string | null) =>
    projects.filter(p => !cpId || !p.customerId || p.customerId === cpId).sort((a, b) => a.name.localeCompare(b.name, 'ru'));

// Существующие разнесения оплаты — в формат сохранения
const allocItems = (p: Payment) => p.allocations.map(a => a.invoiceId ? { invoiceId: a.invoiceId, amount: a.amount }
    : a.openingDebtId ? { openingDebtId: a.openingDebtId, amount: a.amount } : { projectId: a.projectId, amount: a.amount, included: a.included ?? true });

// --- Разнесение оплаты: на счета, долг на начало года или прямо на объекты (для сверки по объектам) ---
const AllocationDialog: React.FC<{ payment: Payment | null; openings: OpeningDebt[]; projects: Project[]; onClose: () => void; onSaved: () => void }> =
    ({ payment, openings, projects, onClose, onSaved }) => {
    const { showNotification } = useData();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [amounts, setAmounts] = useState<Record<string, string>>({});
    const [objects, setObjects] = useState<{ projectId: string; amount: string }[]>([]);
    const cash = payment?.kind === 'NON_TAX';
    useEffect(() => {
        if (!payment) return;
        if (payment.kind === 'NON_TAX') setInvoices([]);
        else api.getInvoices({ counterpartyId: payment.counterpartyId || undefined, scope: payment.organizationId })
            .then(setInvoices).catch(() => setInvoices([]));
        setAmounts(Object.fromEntries(payment.allocations.filter(a => !a.projectId).map(a => [a.invoiceId || `od:${a.openingDebtId}`, String(a.amount)])));
        const own = payment.allocations.filter(a => a.projectId).map(a => ({ projectId: a.projectId as string, amount: String(a.amount) }));
        setObjects(own.length ? own : (payment.allocations.length ? [] : [{ projectId: '', amount: String(payment.amount) }]));
    }, [payment]);
    if (!payment) return null;
    const own = Object.fromEntries(payment.allocations.map(a => [a.invoiceId || `od:${a.openingDebtId}`, a.amount]));
    const targets = cash ? [] : [
        ...openings.filter(o => o.organizationId === payment.organizationId && o.counterpartyId === payment.counterpartyId)
            .map(o => ({ key: `od:${o.id}`, label: `Долг на начало ${o.year} года`, free: o.remaining + (own[`od:${o.id}`] || 0) })),
        ...invoices.map(i => ({ key: i.id, label: `Счёт №${i.number} от ${formatDay(i.date)}${i.appendixNumber ? ` · прил. №${i.appendixNumber}` : ''}`,
                                free: i.remaining + (own[i.id] || 0) })),
    ].filter(t => t.free > 0.004 || t.key in amounts);
    const options = projectsFor(projects, payment.counterpartyId);
    const total = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0) + objects.reduce((s, o) => s + (Number(o.amount) || 0), 0);
    const rest = Math.round((payment.amount - total) * 100) / 100;
    const setObj = (i: number, patch: Partial<{ projectId: string; amount: string }>) => setObjects(list => list.map((o, j) => j === i ? { ...o, ...patch } : o));
    const autofill = () => {
        let left = payment.amount;
        const next: Record<string, string> = {};
        for (const t of targets) { if (left <= 0.004) break; const take = Math.min(left, t.free); next[t.key] = String(Math.round(take * 100) / 100); left -= take; }
        setAmounts(next);
    };
    const save = async () => {
        if (objects.some(o => Number(o.amount) > 0 && !o.projectId)) { showNotification('Выберите объект', 'error'); return; }
        try {
            await api.setAllocations(payment.id, [
                ...Object.entries(amounts).filter(([, v]) => Number(v) > 0).map(([k, v]) =>
                    k.startsWith('od:') ? { openingDebtId: k.slice(3), amount: Number(v) } : { invoiceId: k, amount: Number(v) }),
                ...objects.filter(o => o.projectId && Number(o.amount) > 0).map(o => ({ projectId: o.projectId, amount: Number(o.amount) })),
            ]);
            showNotification('Оплата разнесена', 'success'); onSaved(); onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title={`Разнести оплату ${money(payment.amount)} ₽ от ${formatDay(payment.date)}`} wide>
            <div className="space-y-3 text-sm">
                {payment.description && <p className="text-xs text-slate-500">{payment.description}</p>}
                {!cash && (
                    <table className="w-full text-xs">
                        <thead className="text-slate-500"><tr><Th>На счёт</Th><Th right>Осталось оплатить</Th><Th right>Разнести</Th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {targets.length === 0 && <tr><Td colSpan={3} className="text-center text-slate-400 py-3">Неоплаченных счетов этого контрагента у этого ИП нет</Td></tr>}
                            {targets.map(t => (
                                <tr key={t.key}><Td>{t.label}</Td><Td right><Money value={t.free} /></Td>
                                    <Td right><input type="number" step="0.01" min={0} max={t.free} value={amounts[t.key] || ''} onChange={e => setAmounts(a => ({ ...a, [t.key]: e.target.value }))}
                                        className="w-32 rounded border border-slate-300 px-1.5 py-0.5 text-right font-mono" /></Td></tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <table className="w-full text-xs">
                    <thead className="text-slate-500"><tr><Th>На объект (без счёта){cash ? ' — наличные разносятся только на объекты' : ''}</Th><Th right>Не оплачено по объекту</Th><Th right>Разнести</Th><Th /></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {objects.map((o, i) => {
                            const p = projects.find(x => x.id === o.projectId);
                            const mine = payment.allocations.filter(a => a.projectId === o.projectId).reduce((s, a) => s + a.amount, 0);
                            return (
                                <tr key={i}>
                                    <Td><select value={o.projectId} onChange={e => setObj(i, { projectId: e.target.value })} className="w-full rounded border border-slate-300 px-1.5 py-1 text-xs">
                                        <option value="">Выберите объект</option>
                                        {options.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                                    </select></Td>
                                    <Td right>{p ? <Money value={p.metrics.unpaid + mine} tone="debt" /> : null}</Td>
                                    <Td right className="whitespace-nowrap">
                                        <input type="number" step="0.01" min={0} value={o.amount} onChange={e => setObj(i, { amount: e.target.value })}
                                            className="w-32 rounded border border-slate-300 px-1.5 py-0.5 text-right font-mono" />
                                        {rest > 0.004 && <button type="button" className="ml-1 text-[10px] text-blue-600 hover:underline" title="Добавить нераспределённый остаток"
                                            onClick={() => setObj(i, { amount: String(Math.round(((Number(o.amount) || 0) + rest) * 100) / 100) })}>+остаток</button>}
                                    </Td>
                                    <Td right><button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setObjects(list => list.filter((_, j) => j !== i))}><Trash2 size={12} /></button></Td>
                                </tr>);
                        })}
                    </tbody>
                </table>
                <Button size="sm" variant="outline" onClick={() => setObjects(list => [...list, { projectId: '', amount: rest > 0 ? String(rest) : '' }])}><Plus className="mr-1 h-4 w-4" /> Объект</Button>
                <div className="flex flex-wrap justify-between items-center gap-2 border-t pt-2">
                    <span className="text-xs">Разнесено <strong className="font-mono">{money(total)}</strong> из {money(payment.amount)} · не разнесено <strong className="font-mono">{money(Math.max(0, rest))}</strong></span>
                    <span className="flex gap-2">
                        {!cash && <Button size="sm" variant="outline" onClick={autofill}>Счета по порядку</Button>}
                        <Button size="sm" variant="secondary" onClick={onClose}>Отмена</Button>
                        <Button size="sm" onClick={save} disabled={total > payment.amount + 0.004}>Сохранить</Button>
                    </span>
                </div>
            </div>
        </Modal>
    );
};

const OpeningForm: React.FC<{ item: Partial<OpeningDebt> | null; projects: Project[]; onClose: () => void; onSaved: () => void }> = ({ item, projects, onClose, onSaved }) => {
    const { state, showNotification } = useData();
    const [form, setForm] = useState<Partial<OpeningDebt>>({});
    useEffect(() => { if (item) setForm({ year: new Date().getFullYear(), organizationId: state.organizations[0]?.id, ...item }); }, [item, state.organizations]);
    if (!item) return null;
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        try { await api.saveOpeningDebt(item.id || null, form); showNotification('Сохранено', 'success'); onSaved(); onClose(); }
        catch (err) { showNotification(errorText(err), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title="Долг на начало года">
            <form onSubmit={submit} className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                    <Select label="ИП" value={form.organizationId || ''} onChange={e => setForm({ ...form, organizationId: e.target.value })}
                        options={state.organizations.map(o => ({ value: o.id, label: o.name }))} />
                    <Select label="Контрагент" value={form.counterpartyId || ''} onChange={e => setForm({ ...form, counterpartyId: e.target.value })}
                        options={[{ value: '', label: 'Выберите' }, ...state.counterparties.filter(c => !c.archivedAt && c.type === 'LEGAL_ENTITY' || c.type === 'ENTREPRENEUR').map(c => ({ value: c.id, label: c.name }))]} />
                    <Input label="Год (долг на 1 января)" type="number" value={form.year || ''} onChange={e => setForm({ ...form, year: Number(e.target.value) })} required />
                    <Input label="Сумма, ₽" type="number" step="0.01" value={form.amount ?? ''} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} required />
                </div>
                <Select label="Объект (если разбивка известна)" value={form.projectId || ''} onChange={e => setForm({ ...form, projectId: e.target.value || null })}
                    options={[{ value: '', label: 'Без объекта' }, ...projects.map(p => ({ value: p.id, label: p.name }))]} />
                <Input label="Комментарий" value={form.comment || ''} onChange={e => setForm({ ...form, comment: e.target.value })} />
                <div className="flex justify-end gap-2 pt-2 border-t"><Button type="button" size="sm" variant="secondary" onClick={onClose}>Отмена</Button><Button type="submit" size="sm">Сохранить</Button></div>
            </form>
        </Modal>
    );
};

// --- Оплаты: приходы от контрагентов, их разнесение, переплаты и долги на начало года ---
export const PaymentsTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
    const { revision, showNotification } = useData();
    const names = useNames();
    const scope = useScope();
    const [payments, setPayments] = useState<Payment[]>([]);
    const [openings, setOpenings] = useState<OpeningDebt[]>([]);
    const [onlyFree, setOnlyFree] = useState(false);
    const [searchParams] = useSearchParams();
    const [cpFilter, setCpFilter] = useState(searchParams.get('cp') || '');
    useEffect(() => { const cp = searchParams.get('cp'); if (cp) { setCpFilter(cp); setOnlyFree(true); } }, [searchParams]);
    const [allocating, setAllocating] = useState<Payment | null>(null);
    const [editingOpening, setEditingOpening] = useState<Partial<OpeningDebt> | null>(null);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let alive = true;
        api.getPayments({ scope }).then(p => { if (alive) setPayments(p); }).catch(() => { if (alive) setPayments([]); });
        api.getOpeningDebts().then(o => { if (alive) setOpenings(o); }).catch(() => { if (alive) setOpenings([]); });
        return () => { alive = false; };
    }, [scope, revision, tick]);
    const reload = () => setTick(t => t + 1);
    const noProject = (p: Payment) => (p.noProject || []).reduce((s, x) => s + x.amount, 0);
    const shown = payments.filter(p => (!onlyFree || p.unallocated > 0.004 || noProject(p) > 0.004) && (!cpFilter || p.counterpartyId === cpFilter));
    const free = shown.reduce((s, p) => s + p.unallocated, 0);
    const pName = (id: string) => projects.find(p => p.id === id)?.name || '—';
    const cps = Array.from(new Set(payments.map(p => p.counterpartyId).filter(Boolean))) as string[];
    // Быстро: весь нераспределённый остаток — на выбранный объект
    const toProject = async (p: Payment, projectId: string) => {
        if (!projectId) return;
        try {
            const items = allocItems(p);
            const same = items.find(i => 'projectId' in i && i.projectId === projectId);
            if (same) same.amount = Math.round((same.amount + p.unallocated) * 100) / 100;
            else items.push({ projectId, amount: p.unallocated, included: true });
            await api.setAllocations(p.id, items);
            reload();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    // Счёт реализации без объекта: указать объект реализации — оплаты по счёту станут оплатами объекта
    const invoiceToProject = async (invoiceId: string, projectId: string) => {
        if (!projectId) return;
        try { await api.setInvoiceProject(invoiceId, projectId); reload(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    const visibleOpenings = openings.filter(o => scope === ALL_ORGS || o.organizationId === scope);
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
                <span>Нераспределено (переплаты и авансы): <strong><Money value={free} /> ₽</strong></span>
                <span className="flex flex-wrap items-center gap-3">
                    <select value={cpFilter} onChange={e => setCpFilter(e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs">
                        <option value="">Все контрагенты</option>
                        {cps.map(id => <option key={id} value={id}>{names.cp(id)}</option>)}
                    </select>
                    <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={onlyFree} onChange={e => setOnlyFree(e.target.checked)} /> только с нераспределённым остатком</label>
                </span>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600"><tr><Th>Дата</Th><Th>От кого</Th><Th>ИП</Th><Th>Назначение</Th><Th right>Сумма</Th><Th>Объекты</Th><Th right>Остаток</Th><Th /></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {shown.length === 0 && <tr><Td colSpan={8} className="text-center text-slate-400 py-6">Поступлений от контрагентов нет</Td></tr>}
                        {shown.map(p => (
                            <tr key={p.id}>
                                <Td className="text-xs whitespace-nowrap">{formatDay(p.date)}</Td>
                                <Td className="text-xs">{names.cp(p.counterpartyId)}</Td>
                                <Td className="text-xs">{names.orgShort(p.organizationId)}</Td>
                                <Td className="text-xs text-slate-500 max-w-xs"><div className="truncate" title={p.description}>{p.kind === 'NON_TAX' && <span className="mr-1 rounded bg-slate-100 px-1 text-[10px] text-slate-600">наличные</span>}{p.description}</div></Td>
                                <Td right><Money value={p.amount} /></Td>
                                <Td className="text-xs">
                                    {p.projectShares.map(s => <div key={s.projectId} className="whitespace-nowrap">{pName(s.projectId)}{p.projectShares.length > 1 || p.unallocated > 0.004 ? <span className="text-slate-400"> · {money(s.amount)}</span> : null}</div>)}
                                    {(p.warnings || []).map(w => <div key={w} className="text-[11px] text-red-600">⚠ {w}</div>)}
                                    {p.hint && p.unallocated > 0.004 && (
                                        <button className="mt-0.5 block text-left text-[11px] text-blue-700 hover:underline" title="Объект найден по номеру в назначении платежа"
                                            onClick={() => toProject(p, p.hint!.projectId)}>→ {pName(p.hint.projectId)} (по {p.hint.source})</button>)}
                                    {p.unallocated > 0.004 && (
                                        <select value="" onChange={e => toProject(p, e.target.value)} className="mt-0.5 w-40 rounded border border-amber-300 bg-amber-50 px-1 py-0.5 text-[11px]" title="Весь нераспределённый остаток — на объект">
                                            <option value="">{p.projectShares.length ? 'Остаток на объект…' : 'На объект…'}</option>
                                            {projectsFor(projects, p.counterpartyId).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                                        </select>)}
                                    {(p.noProject || []).map(x => (
                                        <div key={x.invoiceId} className="mt-0.5">
                                            <div className="text-[11px] text-amber-700">счёт №{x.number}{x.appendixNumber ? ` (реализация №${x.appendixNumber})` : ''} без объекта · {money(x.amount)}</div>
                                            <select value="" onChange={e => invoiceToProject(x.invoiceId, e.target.value)} className="w-40 rounded border border-amber-300 bg-amber-50 px-1 py-0.5 text-[11px]"
                                                title="Указать объект у реализации этого счёта — все оплаты по нему станут оплатами объекта">
                                                <option value="">Объект реализации…</option>
                                                {projectsFor(projects, p.counterpartyId).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                            </select>
                                        </div>))}
                                </Td>
                                <Td right>{p.unallocated > 0.004 ? <span className="text-amber-700 font-semibold"><Money value={p.unallocated} /></span> : <Money value={0} tone="muted" />}</Td>
                                <Td right><Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setAllocating(p)}>Разнести</Button></Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Card title="Долг на начало года">
                <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500"><tr><Th>Год</Th><Th>ИП</Th><Th>Контрагент</Th><Th>Объект</Th><Th right>Сумма</Th><Th right>Погашено</Th><Th right>Остаток</Th><Th /></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                        {visibleOpenings.length === 0 && <tr><Td colSpan={8} className="text-center text-slate-400">Не задан</Td></tr>}
                        {visibleOpenings.map(o => (
                            <tr key={o.id}><Td>{o.year}</Td><Td className="text-xs">{names.orgShort(o.organizationId)}</Td><Td className="text-xs">{names.cp(o.counterpartyId)}</Td>
                                <Td className="text-xs">{projects.find(p => p.id === o.projectId)?.name || '—'}</Td>
                                <Td right><Money value={o.amount} /></Td><Td right><Money value={o.paid} tone="good" /></Td><Td right><Money value={o.remaining} tone="debt" /></Td>
                                <Td right className="whitespace-nowrap"><button className="text-slate-400 hover:text-blue-600 mr-2" onClick={() => setEditingOpening(o)}><Pencil size={12} /></button>
                                    <button className="text-slate-400 hover:text-red-600" onClick={async () => { if ((await confirmDialog('Удалить долг на начало года?'))) { await api.deleteOpeningDebt(o.id).catch(e => showNotification(errorText(e), 'error')); reload(); } }}><Trash2 size={12} /></button></Td></tr>
                        ))}
                    </tbody>
                </table>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => setEditingOpening({})}><Plus className="mr-1 h-4 w-4" /> Долг на начало года</Button>
                <p className="text-xs text-slate-500 mt-2">Оплаты прошлогодних счетов разнесите на этот долг кнопкой «Разнести».</p>
            </Card>
            <AllocationDialog payment={allocating} openings={openings} projects={projects} onClose={() => setAllocating(null)} onSaved={reload} />
            <OpeningForm item={editingOpening} projects={projects} onClose={() => setEditingOpening(null)} onSaved={reload} />
        </div>
    );
};
